'use client';
import { hideModal, showToast } from '@/redux/navigationSlice';
import { refreshDocumentsAfterUpload } from '@/redux/prospectDataSlice';
import { RootState } from '@/redux/store';
import { uploadProspectFileClient } from '@/utils/uploadProspectFileClient';
import { Button, Stack, Typography } from '@mui/material';
import { useRouter } from 'next/navigation';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export const UploadFile = (): React.JSX.Element => {
  const router = useRouter();
  const { prospectId } = useSelector((state: RootState) => state.prospectData);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const { documentType } = useSelector((state: RootState) => state.documentData);
  const dispatch = useDispatch();
  const minFileSize = 1024; // 1KB
  const maxFileSize = 15 * 1024 * 1024; // 15MB

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setFile(e.target.files[0]);
    } else {
      setFile(null);
    }
  }, []);

  const handleFileUpload = useCallback(async () => {
    if (!file) {
      dispatch(showToast({ text: 'Por favor, seleccione un archivo antes de subirlo.', kind: 'error' }));
      return;
    }
    if (file && file.size > maxFileSize) {
      dispatch(showToast({ text: 'El archivo excede el tamaño máximo permitido (15MB máximo)', kind: 'error' }));
      return;
    }
    if (file && file.size < minFileSize) {
      dispatch(showToast({ text: 'El archivo es demasiado pequeño (1KB mínimo)', kind: 'error' }));
      return;
    }

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      dispatch(showToast({ text: 'Formato inválido. Solo se permiten PDF, JPG, JPEG o PNG.', kind: 'error' }));
      return;
    }

    if (file && prospectId && documentType) {
      setLoading(true);
      dispatch(showToast({ text: 'Subiendo archivo...', kind: 'info' }));
      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentType', documentType);
      let notAuth = false;
      try {
        const response = await uploadProspectFileClient({ prospectId: prospectId, data: formData });
        if (response.notAuth) {
          notAuth = true;
          dispatch(showToast({ text: 'Usuario no autenticado', kind: 'error' }));
          throw new Error('User not authenticated');
        }
        if (response.message || response.softError || response.hardError) {
          dispatch(
            showToast({
              text: 'Error subiendo el archivo, intente nuevamente',
              kind: 'error',
            }),
          );
          throw new Error('Error uploading file: ' + response.message);
        }
        dispatch(showToast({ text: 'Archivo subido con éxito', kind: 'success' }));
        dispatch(refreshDocumentsAfterUpload(response));
      } catch (error: any) {
        console.error('Error uploading file:', error);
      } finally {
        if (notAuth) {
          router.push('/login');
          router.refresh();
        }
        dispatch(hideModal());
      }
    }
    setLoading(false);
  }, [file, prospectId, documentType, dispatch, router]);

  return (
    <>
      <Typography variant="body1" color="textSecondary">
        Debe ser un archivo PDF o imagen (JPG, JPEG, PNG) de hasta 15MB.
      </Typography>
      {file && file.size > maxFileSize && (
        <Typography variant="body2" color="error" mt={1}>
          El archivo excede el tamaño máximo permitido (15MB).
        </Typography>
      )}
      {file && file.size < minFileSize && (
        <Typography variant="body2" color="error" mt={1}>
          El archivo es demasiado pequeño (1KB mínimo).
        </Typography>
      )}
      <input
        type="file"
        accept=".pdf, .jpg, .jpeg, .png, application/pdf, image/jpeg, image/png"
        id="file-upload"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      <label htmlFor="file-upload">
        <Button variant="contained" color="primary" component="span">
          Explorar...
        </Button>
      </label>
      <Typography variant="body2" color="primary" mt={2}>
        {file ? `Archivo seleccionado: ${file.name}` : 'Ningún archivo seleccionado'}
      </Typography>
      <Stack direction="row" gap={2} width="100%" mt={2} justifyContent="center">
        <Button variant="outlined" onClick={useCallback(() => dispatch(hideModal()), [dispatch])}>
          Cancelar
        </Button>
        <Button disabled={loading} onClick={handleFileUpload}>
          Confirmar
        </Button>
      </Stack>
    </>
  );
};
